import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, Search, X } from 'lucide-react';

import { companiesAPI } from '../../lib/api';
import { inboundRentalsAPI } from '../../lib/inboundRentalsApi';
import {
  conditionsVides,
  corpsDepuisConditions,
  erreursConditions,
} from '../../lib/inboundRentalsForm';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { messageErreur } from '../../lib/erreurs';
import { formatDate } from '../../lib/formats';
import { libelle, referenceLisible, ton } from '../../lib/libelles';
import { useAuth } from '../../context/AuthContext';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { usePaginationServeur } from '../../hooks/usePagination';
import { useDerniereDemande } from '../../hooks/useDerniereDemande';
import { BarrePagination } from '../ui/pagination-bar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { EmptyState } from '../ui/empty-state';
import { Input } from '../ui/input';
import { LoadingState } from '../ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/sonner';
import { SectionCard } from '../dashboard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import InboundConditionsFields from './InboundConditionsFields';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

const STATUTS = ['BROUILLON', 'ACTIF', 'CLOS'];
const NOUVELLE_SOCIETE = '__nouvelle_societe__';

export const companyOption = (company) => ({
  value: company.id,
  label: company.name || 'Société sans nom',
  keywords: [company.city, company.email, company.phone],
});

/**
 * Onglet « Camions que je prends en location » de la page des locations
 * (lot 018): un tableau, une ligne par contrat — Transporteur, N° contrat,
 * Camions, Mode, Début, Fin, État.
 *
 * L'onglet est piloté par l'adresse (`?onglet=entrantes`); le filtre par
 * transporteur aussi (`?societe=…`), pour arriver depuis la page des sociétés.
 */
const InboundRentalsTab = ({ createOpen, onCreateOpenChange }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canSeeMoney } = useAuth();
  const societeFiltre = searchParams.get('societe') || '';

  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [companies, setCompanies] = useState([]);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination commune; `cle` sépare cette liste de celle de l'onglet voisin
  // (`?entrantes-page=`). Une recherche ou un filtre ramène à la première page.
  const pagination = usePaginationServeur(total, {
    cle: 'entrantes',
    reinitialiser: `${debouncedSearch}|${statusFilter}|${societeFiltre}`,
  });
  const { page, limit: pageSize } = pagination;

  const [form, setForm] = useState(conditionsVides);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasFilters = Boolean(debouncedSearch) || statusFilter !== 'all' || Boolean(societeFiltre);

  useEffect(() => {
    let cancelled = false;
    fetchAllPages((params) => companiesAPI.getAllPaged(params))
      .then(({ items }) => {
        if (!cancelled) setCompanies(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const nouvelleDemande = useDerniereDemande();
  const loadData = useCallback(async ({ silencieux = false } = {}) => {
    const perimee = nouvelleDemande();
    try {
      if (!silencieux) setIsLoading(true);
      setLoadFailed(false);
      const params = { page, page_size: pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (societeFiltre) params.company_id = societeFiltre;
      const result = await inboundRentalsAPI.list(params);
      if (perimee()) return;
      setContracts(result.items);
      setTotal(result.total);
    } catch (error) {
      if (perimee()) return;
      setLoadFailed(true);
      toast.error(messageErreur(error, 'Impossible de charger les contrats de location reçue'));
    } finally {
      if (!perimee()) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  }, [page, pageSize, debouncedSearch, statusFilter, societeFiltre, nouvelleDemande]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useRafraichirSur(['locations', 'camions', 'depenses'], loadData);

  const companyOptions = useMemo(
    () => [
      ...companies.map(companyOption),
      { value: NOUVELLE_SOCIETE, label: '+ Ajouter un transporteur', pinned: true },
    ],
    [companies],
  );
  const societeFiltreNom = useMemo(
    () => companies.find((company) => company.id === societeFiltre)?.name,
    [companies, societeFiltre],
  );

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    if (societeFiltre) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('societe');
          return next;
        },
        { replace: true },
      );
    }
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const setPayer = (categorie, porteur) =>
    setForm((prev) => ({ ...prev, payer_matrix: { ...prev.payer_matrix, [categorie]: porteur } }));

  const onCompanyChange = (value) => {
    if (value === NOUVELLE_SOCIETE) {
      navigate('/companies');
      return;
    }
    setField('company_id', value);
  };

  const errors = useMemo(() => erreursConditions(form, { canSeeMoney, nouveau: true }), [form, canSeeMoney]);

  const closeDialog = (open) => {
    onCreateOpenChange(open);
    if (!open) setSubmitAttempted(false);
  };

  const handleCreate = async () => {
    setSubmitAttempted(true);
    if (Object.values(errors).some(Boolean)) {
      toast.error('Complétez les champs signalés');
      return;
    }
    setIsSubmitting(true);
    try {
      const created = await inboundRentalsAPI.create(
        corpsDepuisConditions(form, { canSeeMoney, nouveau: true }),
      );
      toast.success(`Contrat ${referenceLisible(created?.contract_number)} créé`);
      onCreateOpenChange(false);
      setForm(conditionsVides());
      setSubmitAttempted(false);
      navigate(`/rentals/inbound/${created.id}?onglet=camions`);
    } catch (error) {
      toast.error(messageErreur(error, 'Création du contrat impossible'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !hasLoaded) return <LoadingState shape="table" />;

  return (
    <div className="space-y-5" data-testid="inbound-rentals-tab">
      <SectionCard
        icon={KeyRound}
        title="Camions que je prends en location"
        subtitle="Un contrat par transporteur : ses camions, ses conditions, son relevé du mois."
      >
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70"
              aria-hidden="true"
            />
            <Input
              placeholder="Transporteur ou numéro de contrat…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="input-base pl-10"
              aria-label="Rechercher un contrat de location reçue"
              data-testid="inbound-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[170px]" aria-label="Filtrer par état">
              <SelectValue placeholder="Tous les états" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les états</SelectItem>
              {STATUTS.map((code) => (
                <SelectItem key={code} value={code}>
                  {libelle('statut_contrat_entrant', code)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {societeFiltre ? (
            <Badge variant="outline" className="gap-1.5" data-testid="inbound-company-filter">
              Transporteur : {societeFiltreNom || 'sélectionné'}
            </Badge>
          ) : null}
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <X className="h-4 w-4" aria-hidden="true" />
              Effacer
            </Button>
          ) : null}
          <span className="ml-auto text-xs text-muted-foreground">
            {total} contrat{total > 1 ? 's' : ''}
          </span>
        </div>

        {contracts.length === 0 ? (
          loadFailed ? (
            <EmptyState
              variant="error"
              title="Les contrats n’ont pas pu être chargés"
              description="Réessayez dans un instant. Vos contrats ne sont pas perdus."
              actionLabel="Réessayer"
              onAction={loadData}
            />
          ) : hasFilters ? (
            <EmptyState
              variant="filtered"
              icon={KeyRound}
              title="Aucun contrat ne correspond"
              description="Vos contrats ne sont pas perdus : ils sont masqués par les filtres actifs. Effacez-les pour tout revoir."
              actionLabel="Effacer les filtres"
              onAction={resetFilters}
            />
          ) : (
            <EmptyState
              illustration="donnee"
              title="Aucune location reçue"
              description="Quand vous prenez des camions en location chez un transporteur, le contrat fixe le fret, qui paie quoi et la durée. Il produit le relevé du mois à remettre au propriétaire."
              actionLabel={companies.length === 0 ? 'Ajouter un transporteur d’abord' : '+ Nouveau contrat de location reçue'}
              onAction={companies.length === 0 ? () => navigate('/companies') : () => onCreateOpenChange(true)}
            />
          )
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border/70">
            <Table data-testid="inbound-rentals-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Transporteur</TableHead>
                  <TableHead>N° contrat</TableHead>
                  <TableHead className="text-right">Camions</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Début</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead>État</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contracts.map((contract) => (
                  <TableRow
                    key={contract.id}
                    tabIndex={0}
                    role="link"
                    className="cursor-pointer hover:bg-muted/40"
                    data-testid={`inbound-row-${contract.id}`}
                    onClick={() => navigate(`/rentals/inbound/${contract.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') navigate(`/rentals/inbound/${contract.id}`);
                    }}
                  >
                    <TableCell className="font-semibold">{contract.lessor_name || 'Transporteur sans nom'}</TableCell>
                    <TableCell className="font-mono text-xs">{referenceLisible(contract.contract_number)}</TableCell>
                    <TableCell className="text-right tabular-nums">{contract.trucks_count ?? 0}</TableCell>
                    <TableCell>{libelle('mode_remuneration_location', contract.mode)}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">{formatDate(contract.start_date)}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {contract.end_date ? formatDate(contract.end_date) : 'Sans terme'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={ton('statut_contrat_entrant', contract.status)}>
                        {libelle('statut_contrat_entrant', contract.status)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {contracts.length > 0 ? (
          <BarrePagination
            pagination={pagination}
            nom="contrats"
            className="mt-4 rounded-none border-0 border-t border-border/70 bg-transparent px-0 pb-0"
          />
        ) : null}
      </SectionCard>

      <Dialog open={Boolean(createOpen)} onOpenChange={closeDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouveau contrat de location reçue</DialogTitle>
            <DialogDescription>
              Vous prenez des camions en location chez un transporteur. Le contrat démarre en brouillon : vous y
              ajouterez les camions, puis vous l’activerez.
            </DialogDescription>
          </DialogHeader>

          <InboundConditionsFields
            form={form}
            setField={setField}
            setPayer={setPayer}
            canSeeMoney={canSeeMoney}
            idPrefix="nouveau"
            companyOptions={companyOptions}
            onCompanyChange={onCompanyChange}
            errors={errors}
            submitAttempted={submitAttempted}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => closeDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={isSubmitting} data-testid="create-inbound-btn">
              {isSubmitting ? 'Création…' : 'Créer le contrat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InboundRentalsTab;
